import { useDispatch } from 'react-redux';
import { Action } from '@interstate/components/Action';
import { Box } from '@interstate/components/Box';
import { TrashIcon, PencilSquareIcon } from '@interstate/components/Icons';
import type { DataToRemoveTemplate } from '@makemydeal/dr-dash-types';
import * as dealTemplateActionCreators from '@makemydeal/dr-dash-store/actions/dealTemplateActionCreators';
import { Table } from '@interstate/components/Table';
import { Switch } from '@interstate/components/Switch';
import { Tooltip } from '@interstate/components/Tooltip';

import { TemplateName, Modifier } from './cells';
import type { TableData } from '../utils';
import { isMultiScenarioTemplate } from '../utils';
import { TEST_IDS } from '../../constants';

type TemplateListTableProps = {
    data: TableData[];
    setTemplateToRemove: (template: DataToRemoveTemplate) => void;
    handleEditTemplate: (templateId: string) => void;
};
export const TemplateListTable = ({ data, setTemplateToRemove, handleEditTemplate }: TemplateListTableProps) => {
    const dispatch = useDispatch();
    const columns = [
        {
            title: 'Template',
            render: (data: TableData) => {
                const { id, name, description, details } = data;
                return <TemplateName id={id} name={name} description={description} details={details} />;
            }
        },
        {
            dataIndex: 'modifier',
            title: 'Modifier/Date Modified',
            render: (modifier: TableData['modifier'], { id }: TableData) => {
                return Modifier({ ...modifier, id });
            }
        },
        {
            dataIndex: 'isAutoApply',
            title: 'Auto Apply',
            width: 120,
            render: (isAutoApply: boolean, { id, name, inventoryType, scenariosCount }: TableData) => {
                const isMultiScenario = isMultiScenarioTemplate({ scenariosCount });
                const switchElement = (
                    <Switch
                        checked={isAutoApply}
                        disabled={isMultiScenario}
                        onClick={(e) =>
                            dispatch(
                                dealTemplateActionCreators.updateDealTemplate(
                                    { id, isAutoApply: e.target.value === 'checked' },
                                    { name, id, inventoryType }
                                )
                            )
                        }
                        data-testid={TEST_IDS.AUTO_APPLY_SWITCH}
                    />
                );
                return isMultiScenario ? (
                    <div data-testid={TEST_IDS.AUTO_APPLY_DISABLED_TOOLTIP}>
                        <Tooltip
                            toolTipContent="Multi-scenario templates aren't supported with auto apply yet."
                            size="large"
                            position="bottom"
                        >
                            <div>{switchElement}</div>
                        </Tooltip>
                    </div>
                ) : (
                    switchElement
                );
            }
        },
        {
            dataIndex: 'isManualApply',
            title: 'Add To Manual Drop Down',
            width: 180,
            render: (isManualApply: boolean, { id, name, inventoryType }: TableData) => (
                <Switch
                    checked={isManualApply}
                    onClick={(e) =>
                        dispatch(
                            dealTemplateActionCreators.updateDealTemplate(
                                { id, isManualApply: e.target.value === 'checked' },
                                { name, id, inventoryType }
                            )
                        )
                    }
                    data-testid={TEST_IDS.MANUAL_APPLY_SWITCH}
                />
            )
        },
        {
            dataIndex: 'actions',
            title: 'Actions',
            width: 120,
            render: (_: any, record: TableData) => {
                const { id, name, inventoryType } = record;
                const deleteActionDataTestId = TEST_IDS.DELETE_DEAL_TEMPLATE(id);
                const editActionDataTestId = TEST_IDS.EDIT_DEAL_TEMPLATE(id);
                return (
                    <Box sx={{ px: 1, display: 'flex', gap: 1 }}>
                        <Action
                            actionIcon={{ icon: <PencilSquareIcon />, start: true }}
                            id={editActionDataTestId}
                            data-testid={editActionDataTestId}
                            size="lg"
                            onClick={() => {
                                handleEditTemplate(record.id);
                            }}
                        />
                        <Action
                            actionIcon={{ icon: <TrashIcon />, end: true }}
                            id={deleteActionDataTestId}
                            data-testid={deleteActionDataTestId}
                            size="lg"
                            onClick={() => {
                                setTemplateToRemove({ id, name, inventoryType });
                            }}
                        />
                    </Box>
                );
            }
        }
    ];

    return (
        <Box
            sx={{ '& .ant-table .ant-table-container': { overflowX: 'auto!important' } }}
            data-testid={TEST_IDS.TEMPLATE_TABLE_WRAPPER}
            maxHeight={400}
            overflow="auto"
        >
            <Table
                data-testid={TEST_IDS.DEAL_TEMPLATE_LIST}
                data={data}
                tableLayout="auto"
                columns={columns}
                emptyText="No Deal Templates are found"
                enablePagination={false}
            />
        </Box>
    );
};
